/**
 * IdeaTabPanel — the ideas the CEO has decided on, still waiting for a draft.
 *
 * WHY THIS IS A TAB AND NOT A PAGE (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001,
 * CEO ruling 2026-07-30). This surface was a standalone screen at /ideas. The whole flow
 * now lives on the agent page as tabs in the order the work actually moves — IDEA, then
 * PENDING, then SAVED — because a separate idea screen is exactly how five ideas sat nine
 * days unseen. The surface was MOVED here, not copied: the /ideas route, its page
 * component and its module-registry entry are gone, so there is one route, one component
 * and one entry.
 *
 * WHY IT SHOWS EVERY CARD. The CEO had nowhere to look at the five cards minted before the
 * claim repair, so he could not rule on them. This tab IS that place. It shows all of them
 * — the frozen ones and the newly minted one — in one list, unfiltered. Nothing is hidden,
 * collapsed or pushed down the order for looking wrong: a surface that quietly drops what
 * looks wrong is the failure being corrected.
 *
 * WHAT IT MUST NOT DO. It must not stamp any card broken, invalid or superseded. A card
 * whose claim is its headline is FROZEN, not wrong — the claim is computed once at birth
 * and cannot be recomputed — and what happens to those cards is the CEO's separate ruling,
 * which he has not given. Every row says in plain words which kind it is and stops there.
 *
 * Honesty rules, unchanged from the page this replaces:
 *   - A load failure SAYS SO. It never renders an empty table, because "nothing waiting"
 *     and "we could not look" must never look the same.
 *   - An unknown age reads "unknown", never 0 days.
 *   - "Research saved" is the same all-or-nothing verdict drafting uses — a partial set
 *     counts as no set (CEO ruling). Partial is never shown as yes.
 *   - The claim-source is stated as TEXT, never by colour or icon alone: the CEO reads this
 *     surface by text-to-speech.
 *
 * Presentational only. The parent owns the fetch so the tab's count is correct before the
 * tab is ever opened — a count that had to wait for its own tab to be clicked would read
 * as zero until then, which is the defect this window removed from the tab strip.
 */

import React, { useState } from 'react';
import { commissionDifferentIdea, repairIdeaCard } from '@/lib/api';
import { DS_COLORS, DS_TYPOGRAPHY, DS_SPACING, DS_BORDERS } from '@/config/ui-invariants';
import { IDEA_QUEUE_LABELS as LABELS } from '@/config/task-display-config';
import PostStudioCard, { PostStudioCardHeader } from './PostStudioCard';
import IdeaCardRejectMenu from '@/components/shared/IdeaCardRejectMenu';
// WI-IDEA-FULL-READ-AND-CONFIRM-IN-FLIGHT-001 Leg 1b — the ratified numbered-order
// body, REUSED from the Work Inbox's PreDraftIdeaCard rather than rebuilt here (see
// docs/findings/IDEA_TAB_FULL_READ_WIRING_MAP_2026-07-31.md). PreDraftIdeaCard renders
// this body PLUS its own action bar; this tab renders the body alone, under the
// action bar this panel already owns below — one action bar per card, never two.
import { IdeaCardBody } from '@/components/inbox/detailRenderers/LinkedInPostDetail';

// The only sentence assembled here rather than stored: it interpolates two live numbers.
// Its wording lives in LABELS.
const waitingSentence = (n, oldest) =>
  n === 0
    ? LABELS.noneWaiting
    : `${n} ${n === 1 ? 'idea you approved is' : 'ideas you approved are'} waiting for a draft` +
      (oldest != null
        ? `. The oldest has been waiting ${oldest} ${oldest === 1 ? 'day' : 'days'}.`
        : '.');

/**
 * The plain-words answer to "where did this idea's claim come from?".
 *
 * Reads the evidence booleans the route DERIVES by comparing the stored claim against each
 * candidate text still stored on the same card. Checked in this order because the good path
 * is the one worth naming first, and because a short analysis and a headline can in
 * principle produce the same opening sentence.
 */
const CLAIM_SOURCE_KEYS = [
  'claim_composed',
  'claim_from_our_analysis',
  'claim_is_article_headline',
  'claim_from_counter_argument',
];

function claimSourceText(idea) {
  // ABSENCE IS NOT AN EMPTY ANSWER. If the row carries none of the evidence keys, the server
  // did not report a claim source — which is a different fact from "the card's stored texts
  // do not match its claim", and must not borrow that sentence.
  //
  // This is not hypothetical. Observed live on 2026-07-30: the frontend deployed ahead of the
  // backend, the keys were absent, and all five rows read "Cannot be determined from what this
  // card stored" — a statement about the cards that was not true of them. Same shape as a count
  // that reads 0 because nobody answered.
  const reported = CLAIM_SOURCE_KEYS.some((k) => k in idea);
  if (!reported) return LABELS.sourceNotReported;

  // A claim composed from the assembled material matches none of the stored texts by construction (23 Oct 2026).
  if (idea.claim_composed) return LABELS.sourceComposed(idea.claim_composed_unknown);
  if (idea.claim_from_our_analysis) return LABELS.sourceOurAnalysis;
  if (idea.claim_is_article_headline) return LABELS.sourceHeadline;
  if (idea.claim_from_counter_argument) return LABELS.sourceCounterArgument;
  // Reported, and none matched: a real answer about this card.
  return LABELS.sourceUndetermined;
}

/**
 * The card's ACTUAL recorded decision, in plain words.
 *
 * This column used to read `awaiting_draft ? awaiting : undecided`, so every card that was not
 * advancing said "Waiting on your decision" — including one the CEO had already REJECTED. Read
 * aloud, that told him a decided card still needed deciding.
 *
 * The vocabulary is the closed set from IDEA_CARD_CONTRACT §6 (pending | approved | rejected |
 * promoted | system_refused). An ABSENT card_status is a producer gap and never a state (same
 * contract), so it gets its own sentence rather than borrowing "waiting on you".
 *
 * `system_refused` (WI-PROMOTE-THE-HEADLINE-GUARD-AND-CLEAR-THE-PRE-RULE-CARDS-001 Leg 2) is its OWN branch,
 * never folded into `statusRejected` — that word is reserved for a decision the CEO actually
 * made. This is the opposite: the system pulled the card back on its own, before he ever saw it,
 * so it must never read as though he decided anything.
 */
function decisionStateText(idea) {
  const s = idea.card_status;
  // An approved idea that has LEFT the queue without a draft (it carries `approved_outcome`) is not "waiting for a draft"; saying so would be false.
  if ((s === 'approved' || s === 'promoted') && idea.approved_outcome) return LABELS.statusApprovedNoDraft;
  if (s === 'approved') return LABELS.statusApproved;
  if (s === 'promoted') return LABELS.statusPromoted;
  if (s === 'rejected') return LABELS.statusRejected;
  if (s === 'system_refused') return LABELS.statusSystemRefused;
  if (s === 'pending') return LABELS.statusPending;
  return LABELS.statusMissing;
}

/** A card the store can still record a decision against — the gate refuses a second one. */
/**
 * The idea's decision state as a DESIGN_INTENTS key, from declared config.
 * Colour never signals alone here — the pill always carries decisionStateText's own words.
 * WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001.
 */
function decisionStateIntent(idea) {
  const s = idea.card_status;
  const P = LABELS.pillIntents;
  if (s === 'approved') return P.statusApproved;
  if (s === 'promoted') return P.statusPromoted;
  if (s === 'rejected') return P.statusRejected;
  if (s === 'system_refused') return P.statusSystemRefused;
  if (s === 'pending') return P.statusPending;
  return P.statusMissing;
}

/**
 * THE IDEA'S OWN FACTS, AS THE SAVED CARD'S PILL ROW.
 *
 * Same four facts the label-and-value list carried, in the same order — where the claim came
 * from, the decision state, how long it has waited, whether research is saved. Nothing about
 * WHICH facts an idea shows changes; only the card rendering them. Every intent comes from
 * LABELS.pillIntents, so no colour is chosen inside this component.
 */
function ideaPills(idea) {
  const P = LABELS.pillIntents;
  return [
    { text: claimSourceText(idea), intent: P.source },
    { text: decisionStateText(idea), intent: decisionStateIntent(idea) },
    {
      text: idea.age_days == null
        ? LABELS.ageUnknown
        : `${idea.age_days} ${idea.age_days === 1 ? 'day' : 'days'}`,
      intent: P.age,
    },
    {
      text: idea.research_saved ? LABELS.researchYes : LABELS.researchNo,
      intent: idea.research_saved ? P.researchYes : P.researchNo,
    },
  ];
}

function isUndecided(idea) {
  return idea.card_status === 'pending';
}

/* The facts inside a card, as label-and-value lines.
 *
 * THE ONE PLACE "match the other tabs" AND THE DESIGN AUTHORITY DISAGREE, reported rather than
 * resolved by inventing a third style (the dispatch's own instruction).
 *
 * The other tabs label their inner blocks with `text-sm font-bold uppercase tracking-wide`,
 * which is 14px. DESIGN_LANGUAGE_SKILL.md's accessibility floor is "Body text 16px minimum",
 * restated in CLAUDE.md rule 13, and the CEO reads this surface by text-to-speech and residual
 * vision. Copying their 14px label would put this surface back under the floor he asked twice
 * to be raised off.
 *
 * So the label keeps the authority's own body token at the authority's own bold weight —
 * `bodyMd` at 700, which is what the column headers already carry on this surface. That is not
 * a third style: it is the same two values the authority already defines, and no new token,
 * colour or size is introduced. The 14px-uppercase mismatch against the other tabs is stated in
 * the closing report rather than silently split.
 */
const factRow = {
  display: 'flex',
  gap: DS_SPACING.labelToContent + 'px',
  padding: '6px 0',
  alignItems: 'baseline',
};

const factLabel = {
  ...DS_TYPOGRAPHY.bodyMd,
  fontWeight: 700,
  color: DS_COLORS.text.secondary,
  margin: 0,
  minWidth: 210,
  flexShrink: 0,
};

const factValue = {
  ...DS_TYPOGRAPHY.bodyMd,
  color: DS_COLORS.text.primary,
  margin: 0,
};

/** Row action button. 44px floor and 6px radius per DESIGN_LANGUAGE_SKILL.md §4 button shape.
 *  Every intent carries its own WORD (Approve / Promote / Reject) — the colour is a second
 *  signal, never the only one, because this surface is read by text-to-speech. */
const actionButton = (intent) => ({
  ...DS_TYPOGRAPHY.bodyMd,
  fontWeight: 600,
  minHeight: DS_SPACING.minTouchTarget + 'px',
  borderRadius: DS_BORDERS.radiusButton,
  padding: '0 14px',
  marginRight: 8,
  cursor: 'pointer',
  background: intent === 'primary' ? DS_COLORS.text.brand : DS_COLORS.surface.white,
  color: intent === 'primary' ? DS_COLORS.surface.white : DS_COLORS.text.primary,
  border: `1.5px solid ${intent === 'negative' ? DS_COLORS.text.error : DS_COLORS.text.brand}`,
});

const subText = {
  display: 'block',
  ...DS_TYPOGRAPHY.bodySm,
  color: DS_COLORS.text.secondary,
  marginTop: 4,
};

export default function IdeaTabPanel({
  ideas = [],
  summary = null,
  loadFailed = false,
  // The parent owns the decision call and the announcement, the same way it owns the fetch.
  // This panel stays presentational: it decides what is offered, never what is recorded.
  onDecide = null,
  busyId = null,
  /**
   * The status line and the empty sentence, overridable by the surface using this panel.
   *
   * WI-ONE-POST-REACHES-THE-CEO-001 Leg 3. The Rejected > Ideas sub-tab shows THE SAME CARD
   * as the Ideas tab, one decision later, so it must be THIS component — a second idea list
   * is precisely the drift this panel was consolidated to end. What it must NOT inherit is
   * the copy: "Nothing you have approved is waiting for a draft right now" is a lie on a
   * tab of ideas that will never be drafted at all.
   *
   * Defaults are the Ideas tab's existing wording, so that surface is byte-for-byte
   * unchanged and this is purely additive.
   */
  statusLine = null,
  emptyText = null,
}) {
  // Which card's full claim is open. The title truncates like every other tab's, so the
  // expand control is the same control Saved / Posted / Rejected use rather than a new one.
  const [expandedId, setExpandedId] = useState(null);
  // R09: the commission he can ask for on a refused idea, and what came of it, per card.
  const [commissionState, setCommissionState] = useState({});
  // R11: a card that could not be finished stays visible under the same post id and he can ask for another try.
  const [repairState, setRepairState] = useState({});
  const tryRepairAgain = async (idea) => {
    setRepairState((s) => ({ ...s, [idea.post_id]: 'busy' }));
    try {
      const out = await repairIdeaCard(idea.post_id);
      setRepairState((s) => ({ ...s, [idea.post_id]: out?.would_surface ? 'done' : 'still' }));
    } catch (err) {
      setRepairState((s) => ({ ...s, [idea.post_id]: 'failed' }));
    }
  };
  const askForDifferentIdea = async (idea) => {
    setCommissionState((s) => ({ ...s, [idea.post_id]: 'busy' }));
    try {
      const out = await commissionDifferentIdea(idea.post_id);
      setCommissionState((s) => ({ ...s, [idea.post_id]: out?.status === 'already_waiting' ? 'waiting' : 'done' }));
    } catch (err) {
      setCommissionState((s) => ({ ...s, [idea.post_id]: 'failed' }));
    }
  };

  return (
    <section data-testid="idea-tab-panel">
      <div role="status" aria-live="polite" style={{ marginBottom: DS_SPACING.cardGap }}>
        {/* A failure is stated, never rendered as an empty queue. */}
        {loadFailed ? (
          <p
            data-testid="idea-tab-failed"
            role="alert"
            style={{
              ...DS_TYPOGRAPHY.bodyMd,
              color: DS_COLORS.text.error,
              fontWeight: 700,
              margin: 0,
            }}
          >
            {LABELS.failed}
          </p>
        ) : (
          <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: 0 }}>
            {statusLine ?? waitingSentence(summary?.awaiting_draft ?? 0, summary?.oldest_awaiting_days)}
          </p>
        )}
      </div>

      {!loadFailed && ideas.length === 0 && (
        <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: 0 }}>
          {emptyText ?? LABELS.emptyAll}
        </p>
      )}

      {/* THE SAME CARD TREATMENT THE OTHER FOUR TABS USE
          (WI-IDEA-TAB-MATCH-THE-DESIGN-AND-STOP-THE-WRONG-REASON-001, CEO 2026-07-30:
          "use the tokens and UI design we already have for other tabs — let's not reinvent
          what we already have.")

          This was a <table> with its own cell styling while Pending, Saved, Posted and
          Rejected are all `space-y-4` lists of cards. Two components drifting apart is what
          produced the mismatch, so the SHELL is copied verbatim from PendingPostCard /
          SavedPostCard rather than restyled here: white surface, rounded-2xl, a 4px navy left
          accent, 1px borders on the other three sides, `space-y-4` between cards. The title
          uses the same `listItemTitle` token their CollapsibleCardHeader uses.

          What is NOT copied, and why: their header component reads post-only fields
          (post_type, content_type, ranker_score, article_agent_analysis.recommendation) that an
          idea does not have, so reusing it literally would render empty chips or require faking
          post fields on an idea. The shell, spacing, radius, borders, title token and
          empty-state block are all theirs; only the fields inside are the idea's own.

          Their cards also COLLAPSE by default. These deliberately do not: collapsing would put
          the decision state, the wait, the research verdict and the claim source behind a click
          each, and this surface is read by text-to-speech. Their cards collapse the long post
          body, not the facts in their header — nothing here is longer than a sentence. */}
      {!loadFailed && ideas.length > 0 && (
        <div className="space-y-4">
          {/* Every card, in the order the route returns them (oldest first). No filter,
              no collapse, no reordering by how the claim reads. */}
          {ideas.map((idea) => (
            <PostStudioCard
              key={idea.post_id}
              data-idea-id={idea.post_id}
            >
              {/* THE SAVED CARD, NOT A LOOKALIKE
                  (WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001).

                  The claim is the title, the four facts are the pill row, and the article the
                  claim came from is the one truncated summary line — the same three slots, in
                  the same order, that Saved / Posted / Rejected fill. WHICH facts an idea shows
                  is unchanged; only the card rendering them is. The full claim is behind the
                  same expand control every other tab uses, because the title truncates. */}
              <PostStudioCardHeader
                title={idea.claim || idea.source_article_title}
                pills={ideaPills(idea)}
                summaryLine={
                  idea.claim && idea.source_article_title
                    ? `${LABELS.fromArticle} ${idea.source_article_title}`
                    : undefined
                }
                isExpanded={expandedId === idea.post_id}
                onToggle={() =>
                  setExpandedId(expandedId === idea.post_id ? null : idea.post_id)
                }
              />

              {expandedId === idea.post_id && (
                <div
                  data-testid={`idea-claim-full-${idea.post_id}`}
                  style={{
                    ...DS_TYPOGRAPHY.bodyMd,
                    color: DS_COLORS.text.primary,
                    padding: DS_SPACING.cardPadding + 'px',
                    paddingBottom: 0,
                  }}
                >
                  {/* The ratified numbered order — the idea, the case in its three
                      labelled slots, the bar with its per-check reasons, the trigger,
                      the receipt, and the View check. `idea` IS the shared payload
                      (the ideas route and the Work Inbox route now share one producer
                      — see the import above), so it is passed straight through as
                      `item.payload`, exactly as PreDraftIdeaCard does. */}
                  <IdeaCardBody item={{ payload: idea, id: idea.post_id }} />
                  {idea.claim_from_our_analysis && idea.our_analysis_chars > 0 && (
                    <span style={subText}>
                      {LABELS.sourceAnalysisChars(idea.our_analysis_chars)}
                    </span>
                  )}
                </div>
              )}

              {/* R11 — a card the checks could not finish is HERE, under its own post id, with the reason in plain words and a way to
                  try again. It is never a refusal. */}
              {(idea.idea_card?.withhold?.active || idea.idea_card?.repair?.state === 'needs_repair') && (
                <div data-testid={`idea-repair-${idea.post_id}`} style={{ padding: DS_SPACING.cardPadding + 'px', paddingBottom: 0 }}>
                  <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary, fontWeight: 700, margin: 0 }}>{LABELS.repairHeading}</p>
                  {Array.isArray(idea.idea_card?.withhold?.objections) && idea.idea_card.withhold.objections.length > 0 && (
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: '4px 0 0' }}>
                      {LABELS.repairMissing}: {idea.idea_card.withhold.objections.map((o) => String(o).trim().replace(/\.$/, '')).join('; ')}.
                    </p>
                  )}
                  {idea.idea_card.repair?.state === 'needs_repair' && (
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: '4px 0 0' }}>
                      {LABELS.repairStopped.replace('{attempts}', String(idea.idea_card.repair.attempts ?? ''))}
                    </p>
                  )}
                  <button
                    type="button"
                    data-testid={`idea-repair-button-${idea.post_id}`}
                    disabled={repairState[idea.post_id] === 'busy'}
                    onClick={() => tryRepairAgain(idea)}
                    style={{ ...actionButton('neutral'), marginTop: 8 }}
                  >
                    {repairState[idea.post_id] === 'busy' ? LABELS.repairBusy : LABELS.actRepair}
                  </button>
                  <p data-testid={`idea-repair-result-${idea.post_id}`} role="status" aria-live="polite" style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: '8px 0 0' }}>
                    {{ done: LABELS.repairDone, still: LABELS.repairStill, failed: LABELS.repairFailed }[repairState[idea.post_id]] || ''}
                  </p>
                </div>
              )}

              {/* An idea he approved that left the queue with no draft: the sentence the server built from the record's own stored reason
                  (and, where a newer attempt replaced it, where that attempt ended). Words only; there is nothing here to press. */}
              {idea.approved_outcome?.sentence && (
                <div data-testid={`idea-outcome-${idea.post_id}`} style={{ padding: DS_SPACING.cardPadding + 'px', paddingBottom: 0 }}>
                  <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary, fontWeight: 700, margin: 0 }}>{LABELS.outcomeHeading}</p>
                  <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: '4px 0 0' }}>{idea.approved_outcome.sentence}</p>
                </div>
              )}

              {/* THE CONTROLS. Only the three actions the store can already record —
                  ceo_decision in approve|reject|promote (IDEA_CARD_CONTRACT §6), written by
                  the existing work_inbox decision sites. No new state, no bulk action: each
                  card is decided on its own, by him.
                  A card that already carries a decision gets no buttons — the backend refuses
                  a second one with a 409, so offering them would be a ghost. */}
              <div
                data-testid="idea-decision-controls"
                style={{ padding: DS_SPACING.cardPadding + 'px' }}
              >
                {isUndecided(idea) ? (
                  <>
                    <button
                      type="button"
                      data-testid={`idea-approve-${idea.post_id}`}
                      disabled={busyId === idea.post_id}
                      onClick={() => onDecide && onDecide(idea, 'approve')}
                      style={actionButton('primary')}
                    >
                      {busyId === idea.post_id ? LABELS.actBusy : LABELS.actApprove}
                    </button>
                    <button
                      type="button"
                      data-testid={`idea-promote-${idea.post_id}`}
                      disabled={busyId === idea.post_id}
                      onClick={() => onDecide && onDecide(idea, 'promote')}
                      style={actionButton('neutral')}
                    >
                      {LABELS.actPromote}
                    </button>
                    {/* THE REJECT MENU BELONGS TO ITS CARD, NOT TO THE PAGE
                        (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 3a).

                        Pressing Reject used to set a PAGE-level `rejectingIdea`, and the menu
                        then rendered in a panel at the top of the tab — above the whole list —
                        while this card kept its own Reject button sitting underneath. One
                        action, two places on screen at once, and the panel named the card only
                        by repeating its claim as text.

                        It is the same ONE menu and the same ONE fetcher (the shared
                        IdeaCardRejectMenu, reading the backend's own idea-card vocabulary);
                        only its HOME moved. A dropdown anchored to its own trigger cannot
                        detach from the card it belongs to, which is what makes the defect
                        unrepresentable rather than merely fixed. */}
                    <span
                      data-testid={`idea-reject-${idea.post_id}`}
                      style={{ display: 'inline-block', marginRight: 8 }}
                    >
                      <IdeaCardRejectMenu
                        disabled={busyId === idea.post_id}
                        onPick={(codeId) => onDecide && onDecide(idea, 'reject', codeId)}
                      />
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary }}>
                      {LABELS.decidedAlready}
                    </span>
                    {/* R09: a refused idea stays refused. The one way to get a different idea from the same article is to ask
                        for it here; nothing runs by itself. */}
                    {idea.card_status === 'rejected' && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          data-testid={`idea-commission-${idea.post_id}`}
                          disabled={commissionState[idea.post_id] === 'busy' || commissionState[idea.post_id] === 'done'}
                          onClick={() => askForDifferentIdea(idea)}
                          style={actionButton('neutral')}
                        >
                          {commissionState[idea.post_id] === 'busy' ? LABELS.commissionBusy : LABELS.actCommission}
                        </button>
                        {/* The live region exists before its text does, so a screen reader announces the result. */}
                        <p data-testid={`idea-commission-result-${idea.post_id}`} role="status" aria-live="polite" style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, margin: '8px 0 0' }}>
                          {{ done: LABELS.commissionDone, waiting: LABELS.commissionWaiting, failed: LABELS.commissionFailed }[commissionState[idea.post_id]] || ''}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </PostStudioCard>
          ))}
        </div>
      )}

    </section>
  );
}
